"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import {
  getPostHog,
  identifyUser,
  initPostHog,
  resetUser,
} from "@/lib/posthog";
import { track } from "@/lib/analytics";
import { analyticsIdentity } from "@/lib/seats/session";

/** Written by the register form / Google button just before the sign-in redirect. */
export const SIGNUP_METHOD_KEY = "vr_signup_method";
/** Accounts younger than this at first identify count as "just signed up". */
const NEW_ACCOUNT_WINDOW_MS = 15 * 60 * 1000;

function readSignupMethod(): "credentials" | "google" | "unknown" {
  try {
    const v = window.sessionStorage.getItem(SIGNUP_METHOD_KEY);
    window.sessionStorage.removeItem(SIGNUP_METHOD_KEY);
    return v === "credentials" || v === "google" ? v : "unknown";
  } catch {
    return "unknown";
  }
}

/** Once per account, ever — survives reloads via localStorage. */
function claimSignupOnce(userId: string): boolean {
  const key = `vr_signup_tracked:${userId}`;
  try {
    if (window.localStorage.getItem(key)) return false;
    window.localStorage.setItem(key, "1");
    return true;
  } catch {
    return false;
  }
}

/**
 * PostHog lifecycle bound to the React tree.
 *
 * - Inits on first mount (no-op if env key missing).
 * - Captures `$pageview` on App Router route changes — Next 15+ doesn't
 *   fire native page events because there's no full page reload, so we
 *   subscribe to `usePathname` + `useSearchParams` here.
 * - Calls `posthog.identify` whenever the NextAuth session resolves to a
 *   logged-in user, and `posthog.reset` when they sign out so the next
 *   anonymous session doesn't inherit identity.
 *
 * Mounted inside SessionProvider in `providers.tsx`. Renders nothing by
 * design — pure side-effects component.
 */
export function PostHogTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const lastIdentifiedUserId = useRef<string | null>(null);

  // Seats: the invitation page's address carries a one-time token until the page strips it, so this
  // tracker never starts PostHog there and sends no pageview; the page itself starts it right after
  // the strip (app/invite/accept/page.tsx) and sends its own events.
  const offRecord = (pathname ?? "").startsWith("/invite");

  useEffect(() => {
    if (!offRecord) initPostHog();
  }, [offRecord]);

  // Pageview capture. Combine pathname + searchParams so e.g.
  // /a2p/onboarding?step=businessUseCase records as a distinct page from
  // the same wizard's other steps.
  useEffect(() => {
    const ph = getPostHog();
    if (!ph || offRecord) return;
    const url = window.location.origin + pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "");
    ph.capture("$pageview", { $current_url: url });
    // Record the signup form for ANONYMOUS visitors too. Replay is otherwise
    // auth-gated (see identity sync below), which left the biggest measured
    // signup drop unwatchable: in Aug 2026, 18 of 21 people who clicked the
    // marketing CTA reached /login and clicked nothing, and there was no
    // recording to say why. maskAllInputs is on at init, so the email and
    // password fields are black boxes in the replay. If the visitor then
    // registers, identify() merges this session into their person.
    if (pathname === "/login") ph.startSessionRecording();
  }, [pathname, searchParams, offRecord]);

  // Identity sync.
  useEffect(() => {
    const ph = getPostHog();
    if (!ph) return;

    // Seats: identify the PERSON; the account rides as a property (lib/seats/session.ts).
    const identity = analyticsIdentity(session);
    if (status === "authenticated" && session?.user && identity) {
      // Replay is gated to logged-in users (disabled at init). Start it once a
      // session is authenticated; posthog-js no-ops if already recording, so
      // calling it on every authenticated render is safe.
      ph.startSessionRecording();
      if (lastIdentifiedUserId.current !== identity.personId) {
        identifyUser(identity.personId, {
          email: session.user.email ?? undefined,
          name: session.user.name ?? undefined,
          isInternal: session.user.isInternal ?? false,
          signupAt: session.user.createdAt ?? null,
          phoneVerified: session.user.phoneVerified ?? false,
          accountId: identity.accountId,
          role: identity.role,
        });
        lastIdentifiedUserId.current = identity.personId;
        // `user_signed_up` — PostHog's docs say not to rely on autocapture for
        // this, and nothing fired it before 2026-08-29. Emitted HERE, after
        // identify, so it lands on the merged person (anonymous marketing
        // pageviews + the new identity) and carries the acquisition context.
        // Covers both /api/register and Google OAuth: the account is "new" if
        // it was created within the last 15 minutes and we have not claimed it.
        // A team member who just joined is not a signup (team_invite_joined covers them).
        const createdAt = session.user.createdAt ? Date.parse(session.user.createdAt) : NaN;
        if (
          identity.role === "owner" &&
          Number.isFinite(createdAt) &&
          Date.now() - createdAt < NEW_ACCOUNT_WINDOW_MS &&
          claimSignupOnce(identity.personId)
        ) {
          track("user_signed_up", { method: readSignupMethod() });
        }
      }
    } else if (status === "unauthenticated" && lastIdentifiedUserId.current) {
      // Stop recording before reset so the next anonymous session isn't
      // recorded and doesn't inherit the prior identity.
      ph.stopSessionRecording();
      resetUser();
      lastIdentifiedUserId.current = null;
    }
    // offRecord: PostHog may start only AFTER the session resolves (an invitee who joins with a
    // password is signed in on /invite, then moved to the app), and this must run again then, or
    // their first session stays anonymous and unrecorded until a reload.
  }, [status, session?.user?.id, session?.user?.actorId, session?.user?.email, session?.user?.name, offRecord]);

  return null;
}
